import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Building2,
  ArrowRight,
  FileText,
  Scale,
  FileStack,
  CircleDollarSign,
  Landmark,
  Receipt,
  Calculator,
  Wallet,
  ListChecks,
  UserPlus,
  FileSignature,
  AlertTriangle,
  Clock,
  ShoppingCart,
  Package,
  PackageX,
  Truck,
  Banknote,
  ClipboardList,
} from "lucide-react";
import { apiClient } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import {
  useDashboardSummary,
  type DashboardMonthlyActivityRow,
  type StoreDashboardSummary,
} from "@/api/dashboard";
import { useAuditLogs } from "@/api/audit";
import type { ApiSuccessResponse } from "@erp/shared";

interface OrganizationSummary {
  id: string;
  name: string;
  status: string;
  slug: string;
  taxId?: string | null;
}

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

// Paleta categórica validada (dataviz skill — references/palette.md), orden fijo, nunca reordenada por dato.
const CATEGORICAL = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#94a3b8"];

const ACTIVITY_TABS: Array<{ key: keyof Omit<DashboardMonthlyActivityRow, "month">; label: string; format: "count" | "currency" }> = [
  { key: "documentos", label: "Documentos", format: "count" },
  { key: "facturacion", label: "Facturación", format: "currency" },
  { key: "cartera", label: "Cartera", format: "currency" },
  { key: "procesos", label: "Procesos", format: "count" },
];

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Hace un momento";
  if (mins < 60) return `Hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `Hace ${days} d`;
}

const ACTION_LABEL: Record<string, string> = {
  CREATE: "creó",
  UPDATE: "actualizó",
  DELETE: "eliminó",
  DOWNLOAD: "descargó",
  UPLOAD: "subió",
  LOGIN: "inició sesión",
  LOGOUT: "cerró sesión",
  APPROVE: "aprobó",
  EXPORT: "exportó",
};

/**
 * Dashboard (rediseño a pedido explícito del usuario, que compartió una
 * maqueta visual de referencia): panel de bienvenida + KPIs + evolución
 * mensual + distribución de documentos + vencimientos + accesos rápidos +
 * actividad reciente. Cada número sale de `/dashboard/summary` (datos
 * reales de la organización) — nunca datos de relleno (regla #57).
 */
export default function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canSeeSummary = hasPermission("reports.read");
  const canSeeAudit = hasPermission("audit.read");

  const [activeTab, setActiveTab] = useState<(typeof ACTIVITY_TABS)[number]["key"]>("documentos");

  const { data: organization } = useQuery({
    queryKey: ["organizations", "current"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<OrganizationSummary>>("/organizations/current");
      return data.data;
    },
  });

  const { data: summary, isLoading: summaryLoading } = useDashboardSummary({ enabled: canSeeSummary });
  const { data: recentAudit } = useAuditLogs(1, {}, { enabled: canSeeAudit, pageSize: 6 });

  const now = new Date();
  const dateLabel = now.toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const timeLabel = now.toLocaleTimeString("es-CO", { hour: "numeric", minute: "2-digit" });

  const isStore = summary?.kind === "store";
  const firm = summary?.kind === "firm" ? summary.firm : undefined;
  const store = summary?.kind === "store" ? summary.store : undefined;

  const FIRM_SHORTCUTS = [
    { to: "/clients", label: "Nuevo cliente", icon: UserPlus, permission: "clients.create" },
    { to: "/document-generator", label: "Generar documento", icon: FileSignature, permission: "documents.create" },
    { to: "/invoices", label: "Nueva factura", icon: Receipt, permission: "invoices.create" },
    { to: "/procedures", label: "Registrar trámite", icon: FileStack, permission: "procedures.create" },
    { to: "/legal-cases", label: "Nuevo caso jurídico", icon: Scale, permission: "legal.create" },
    { to: "/accounting/entries", label: "Asiento contable", icon: Calculator, permission: "accounting.create" },
    { to: "/payments", label: "Registrar pago", icon: Wallet, permission: "payments.create" },
    { to: "/tasks", label: "Crear tarea", icon: ListChecks, permission: "tasks.create" },
  ];

  const STORE_SHORTCUTS = [
    { to: "/store/pos", label: "Nueva venta", icon: ShoppingCart, permission: "pos.create" },
    { to: "/store/products", label: "Nuevo producto", icon: Package, permission: "inventory.create" },
    { to: "/store/purchases", label: "Registrar compra", icon: Truck, permission: "purchases.create" },
    { to: "/store/pos", label: "Abrir/cerrar caja", icon: Banknote, permission: "pos.create" },
  ];

  const shortcuts = (isStore ? STORE_SHORTCUTS : FIRM_SHORTCUTS).filter((s) => hasPermission(s.permission));

  const distribution = useMemo(() => {
    const rows = firm?.documentDistribution ?? [];
    const top = rows.slice(0, 4);
    const restCount = rows.slice(4).reduce((sum, r) => sum + r.count, 0);
    const combined = restCount > 0 ? [...top, { category: "OTROS", label: "Otros", count: restCount }] : top;
    const total = combined.reduce((sum, r) => sum + r.count, 0);
    return { rows: combined, total };
  }, [firm?.documentDistribution]);

  const kpiCards = firm
    ? [
        { label: "Clientes activos", value: firm.kpis.clientsActive, icon: Building2, tone: "brand", permission: "clients.read", to: "/clients" },
        { label: "Documentos", value: firm.kpis.documentsGenerated, icon: FileText, tone: "teal", permission: "documents.read", to: "/documents" },
        { label: "Procesos jurídicos", value: firm.kpis.legalCasesActive, icon: Scale, tone: "violet", permission: "legal.read", to: "/legal-cases" },
        { label: "Trámites en curso", value: firm.kpis.proceduresInProgress, icon: FileStack, tone: "amber", permission: "procedures.read", to: "/procedures" },
        {
          label: "Ingresos del mes",
          value: currency.format(firm.kpis.revenueThisMonth),
          icon: CircleDollarSign,
          tone: "emerald",
          permission: "invoices.read",
          to: "/reports",
        },
      ].filter((k) => hasPermission(k.permission))
    : [];

  const storeKpiCards = store
    ? [
        {
          label: "Ventas de hoy",
          value: currency.format(store.kpis.todaySalesTotal),
          hint: `${store.kpis.todaySalesCount} venta${store.kpis.todaySalesCount === 1 ? "" : "s"}`,
          icon: ShoppingCart,
          tone: "emerald",
          permission: "pos.read",
          to: "/store/pos",
        },
        { label: "Productos activos", value: store.kpis.activeProducts, icon: Package, tone: "brand", permission: "inventory.read", to: "/store/products" },
        {
          label: "Bajo stock",
          value: store.kpis.lowStockCount,
          icon: PackageX,
          tone: store.kpis.lowStockCount > 0 ? "red" : "teal",
          permission: "inventory.read",
          to: "/store/products",
        },
        { label: "Proveedores activos", value: store.kpis.activeSuppliers, icon: Truck, tone: "violet", permission: "purchases.read", to: "/store/purchases" },
        {
          label: "Compras pendientes",
          value: store.kpis.pendingPurchases,
          hint: "Sin recibir",
          icon: ClipboardList,
          tone: "amber",
          permission: "purchases.read",
          to: "/store/purchases",
        },
      ].filter((k) => hasPermission(k.permission))
    : [];

  const toneStyles: Record<string, string> = {
    brand: "bg-brand-600",
    teal: "bg-teal-600",
    violet: "bg-violet-600",
    amber: "bg-amber-500",
    emerald: "bg-emerald-600",
    red: "bg-red-600",
  };

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-brand-950 via-brand-800 to-brand-600 p-6 text-white">
        <div className="relative z-10 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <p className="text-sm font-medium text-brand-200">
              {organization?.name}
              {organization?.taxId ? ` · NIT ${organization.taxId}` : ""}
            </p>
            <h1 className="mt-1 text-2xl font-semibold">Bienvenido, {user?.fullName?.split(" ")[0]} 👋</h1>
            <p className="mt-1 text-sm text-brand-200">Todo su negocio en un solo sistema.</p>
          </div>
          <div className="rounded-lg bg-white/10 px-4 py-2.5 text-right backdrop-blur-sm">
            <p className="text-xs capitalize text-brand-200">{dateLabel}</p>
            <p className="text-sm font-semibold">{timeLabel}</p>
            <p className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Sistema en línea
            </p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      {canSeeSummary && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {summaryLoading && Array.from({ length: 5 }).map((_, i) => <div key={i} className="card h-28 animate-pulse p-5" />)}
          {(isStore ? storeKpiCards : kpiCards).map((k) => {
            const Icon = k.icon;
            return (
              <Link key={k.label} to={k.to} className="card group flex flex-col justify-between p-5 transition-all hover:-translate-y-0.5 hover:shadow-md">
                <div className={`flex h-10 w-10 items-center justify-center rounded-lg text-white ${toneStyles[k.tone]}`}>
                  <Icon size={20} />
                </div>
                <div className="mt-4">
                  <p className="text-xl font-semibold text-gray-900">{k.value}</p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {k.label}
                    {"hint" in k && k.hint ? ` · ${k.hint}` : ""}
                  </p>
                </div>
                <div className="mt-3 flex items-center gap-1 text-xs font-medium text-brand-600 opacity-0 transition-opacity group-hover:opacity-100">
                  Ver detalle <ArrowRight size={12} />
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {canSeeSummary && firm && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="card p-5 lg:col-span-2">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Evolución de actividad</h2>
              <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
                {ACTIVITY_TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                      activeTab === t.key ? "bg-white text-brand-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
                    }`}
                    onClick={() => setActiveTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <ActivityLineChart data={firm.monthlyActivity} metric={activeTab} format={ACTIVITY_TABS.find((t) => t.key === activeTab)!.format} />
          </div>

          <div className="card p-5">
            <h2 className="text-sm font-semibold text-gray-900">Distribución de documentos</h2>
            {distribution.total === 0 ? (
              <p className="mt-6 text-center text-sm text-gray-400">Aún no hay documentos activos.</p>
            ) : (
              <DocumentDonut rows={distribution.rows} total={distribution.total} />
            )}
          </div>
        </div>
      )}

      {canSeeSummary && store && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="card p-5 lg:col-span-2">
            <h2 className="text-sm font-semibold text-gray-900">Ventas de los últimos 7 días</h2>
            <SalesTrendChart data={store.salesLast7Days} />
          </div>

          <div className="card p-5">
            <h2 className="text-sm font-semibold text-gray-900">Turno de caja</h2>
            <CashShiftPanel cashShift={store.cashShift} />
          </div>
        </div>
      )}

      {canSeeSummary && store && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="card p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Ventas recientes</h2>
              <Link to="/store/pos" className="text-xs font-medium text-brand-600 hover:text-brand-800">
                Ir al punto de venta
              </Link>
            </div>
            <RecentSalesList sales={store.recentSales} />
          </div>

          <div className="card p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Bajo stock</h2>
              <Link to="/store/products" className="text-xs font-medium text-brand-600 hover:text-brand-800">
                Ver productos
              </Link>
            </div>
            <LowStockList products={store.lowStockProducts} byBranch={store.lowStockByBranch} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Accesos rápidos */}
        {shortcuts.length > 0 && (
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-gray-900">Accesos rápidos</h2>
            <div className="mt-3 space-y-1">
              {shortcuts.map((s) => {
                const Icon = s.icon;
                return (
                  <Link
                    key={s.to + s.label}
                    to={s.to}
                    className="group flex items-center justify-between rounded-lg px-2.5 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <span className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-50 text-brand-600">
                        <Icon size={15} />
                      </span>
                      {s.label}
                    </span>
                    <ArrowRight size={14} className="text-gray-300 opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Vencimientos y alertas (firma contable/jurídica) */}
        {canSeeSummary && firm && (
          <div className="card p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Vencimientos y alertas</h2>
              <Link to="/reports" className="text-xs font-medium text-brand-600 hover:text-brand-800">
                Ver todas
              </Link>
            </div>
            <div className="mt-3 space-y-2">
              <DeadlineRow icon={Landmark} label="Obligaciones tributarias" hint="Próximos 7 días" value={firm.deadlineSummary.obligacionesTributarias} />
              <DeadlineRow icon={Scale} label="Términos judiciales" hint="Por vencer (30 días)" value={firm.deadlineSummary.terminosJudiciales} />
              <DeadlineRow icon={FileStack} label="Trámites pendientes" hint="En seguimiento" value={firm.deadlineSummary.tramitesPendientes} />
              <DeadlineRow icon={Receipt} label="Facturas por cobrar" hint="Vencidas" value={firm.deadlineSummary.facturasPorCobrarVencidas} critical />
            </div>
            <div className="mt-4 border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Cumplimiento de vencimientos</span>
                <span className="font-semibold text-gray-900">{firm.complianceRate}%</span>
              </div>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className={`h-full rounded-full ${firm.complianceRate >= 80 ? "bg-emerald-500" : firm.complianceRate >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                  style={{ width: `${firm.complianceRate}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Productos con más rotación (tienda) */}
        {canSeeSummary && store && (
          <div className="card p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Productos con más rotación</h2>
              <Link to="/store/products" className="text-xs font-medium text-brand-600 hover:text-brand-800">
                Ver todos
              </Link>
            </div>
            <TopProductsList products={store.topProducts} />
          </div>
        )}

        {/* Actividad reciente */}
        {canSeeAudit && (
          <div className="card p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Actividad reciente</h2>
              <Link to="/audit" className="text-xs font-medium text-brand-600 hover:text-brand-800">
                Ver todas
              </Link>
            </div>
            <div className="mt-3 space-y-3">
              {(!recentAudit || recentAudit.data.length === 0) && <p className="text-sm text-gray-400">Sin actividad reciente.</p>}
              {recentAudit?.data.map((log) => (
                <div key={log.id} className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-500">
                    <Clock size={13} />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-xs text-gray-700">
                      <span className="font-medium text-gray-900">{log.user?.fullName ?? "Sistema"}</span> {ACTION_LABEL[log.action] ?? log.action.toLowerCase()}{" "}
                      {log.entityType}
                    </p>
                    <p className="text-[11px] text-gray-400">{timeAgo(log.createdAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {!canSeeSummary && (
        <div className="card border-dashed p-5">
          <p className="text-sm text-gray-500">
            Los indicadores de gestión se muestran a los usuarios con permiso de Reportes.
          </p>
        </div>
      )}
    </div>
  );
}

function DeadlineRow({
  icon: Icon,
  label,
  hint,
  value,
  critical = false,
}: {
  icon: typeof Landmark;
  label: string;
  hint: string;
  value: number;
  critical?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg px-1 py-1.5">
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${
          critical && value > 0 ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"
        }`}
      >
        {value > 0 && critical ? <AlertTriangle size={15} /> : <Icon size={15} />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-[11px] text-gray-400">{hint}</p>
      </div>
      <span className={`text-lg font-semibold ${critical && value > 0 ? "text-red-600" : "text-gray-900"}`}>{value}</span>
    </div>
  );
}

function ActivityLineChart({
  data,
  metric,
  format,
}: {
  data: DashboardMonthlyActivityRow[];
  metric: keyof Omit<DashboardMonthlyActivityRow, "month">;
  format: "count" | "currency";
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const width = 560;
  const height = 220;
  const padding = { top: 16, right: 12, bottom: 24, left: 12 };
  const values = data.map((d) => d[metric]);
  const maxVal = Math.max(...values, 1);
  const plotW = width - padding.left - padding.right;
  const plotH = height - padding.top - padding.bottom;

  const points = data.map((d, i) => {
    const x = padding.left + (plotW * i) / Math.max(data.length - 1, 1);
    const y = padding.top + plotH - (plotH * d[metric]) / maxVal;
    return { x, y, value: d[metric], month: d.month };
  });

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1]?.x ?? 0} ${padding.top + plotH} L ${points[0]?.x ?? 0} ${padding.top + plotH} Z`;

  const formatValue = (v: number) => (format === "currency" ? currency.format(v) : String(v));

  return (
    <div className="relative mt-3">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label="Evolución mensual">
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line
            key={f}
            x1={padding.left}
            x2={width - padding.right}
            y1={padding.top + plotH * (1 - f)}
            y2={padding.top + plotH * (1 - f)}
            stroke="#f1f5f9"
            strokeWidth={1}
          />
        ))}
        <path d={areaPath} fill="#2a78d6" opacity={0.06} />
        <path d={linePath} fill="none" stroke="#2a78d6" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={hoverIndex === i ? 5 : 3} fill="#2a78d6" stroke="white" strokeWidth={1.5} />
            <circle
              cx={p.x}
              cy={p.y}
              r={12}
              fill="transparent"
              onMouseEnter={() => setHoverIndex(i)}
              onMouseLeave={() => setHoverIndex((cur) => (cur === i ? null : cur))}
              style={{ cursor: "pointer" }}
            />
            <text x={p.x} y={height - 4} textAnchor="middle" fontSize={10} fill="#94a3b8">
              {p.month}
            </text>
          </g>
        ))}
      </svg>
      {hoverIndex !== null && points[hoverIndex] && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-md bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-lg"
          style={{ left: `${(points[hoverIndex].x / width) * 100}%`, top: `${(points[hoverIndex].y / height) * 100 - 2}%` }}
        >
          {points[hoverIndex].month}: {formatValue(points[hoverIndex].value)}
        </div>
      )}
    </div>
  );
}

function DocumentDonut({ rows, total }: { rows: Array<{ category: string; label: string; count: number }>; total: number }) {
  const radius = 15.915; // circunferencia = 100 con pathLength implícito (2*pi*r ~= 100)
  const gap = 1.2;
  let cumulative = 0;
  const segments = rows.map((r, i) => {
    const pct = (r.count / total) * 100;
    const length = Math.max(pct - gap, 0);
    const offset = cumulative;
    cumulative += pct;
    return { ...r, pct, length, offset, color: CATEGORICAL[i % CATEGORICAL.length] };
  });

  return (
    <div className="mt-3">
      <div className="relative mx-auto h-40 w-40">
        <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
          <circle cx="18" cy="18" r={radius} fill="none" stroke="#f1f5f9" strokeWidth={5} />
          {segments.map((s) => (
            <circle
              key={s.category}
              cx="18"
              cy="18"
              r={radius}
              fill="none"
              stroke={s.color}
              strokeWidth={5}
              strokeDasharray={`${s.length} ${100 - s.length}`}
              strokeDashoffset={-s.offset}
              strokeLinecap="butt"
            />
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-semibold text-gray-900">{total}</span>
          <span className="text-[10px] text-gray-400">Total</span>
        </div>
      </div>
      <div className="mt-4 space-y-1.5">
        {segments.map((s) => (
          <div key={s.category} className="flex items-center justify-between text-xs">
            <span className="flex min-w-0 items-center gap-1.5 text-gray-600">
              <span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: s.color }} />
              <span className="truncate">{s.label}</span>
            </span>
            <span className="shrink-0 font-medium text-gray-900">
              {Math.round(s.pct)}% ({s.count})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Widgets del Dashboard de tienda (víveres/abarrotes, supermercado y
// demás industrias comerciales — ver dashboard.service.ts::getStoreDashboardSummary) ──

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  EFECTIVO: "Efectivo",
  TRANSFERENCIA: "Transferencia",
  CHEQUE: "Cheque",
  TARJETA: "Tarjeta",
  OTRO: "Otro",
};

function SalesTrendChart({ data }: { data: StoreDashboardSummary["salesLast7Days"] }) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const width = 560;
  const height = 220;
  const padding = { top: 16, right: 12, bottom: 24, left: 12 };
  const maxVal = Math.max(...data.map((d) => d.total), 1);
  const plotW = width - padding.left - padding.right;
  const plotH = height - padding.top - padding.bottom;
  const hasSales = data.some((d) => d.total > 0);

  const points = data.map((d, i) => {
    const x = padding.left + (plotW * i) / Math.max(data.length - 1, 1);
    const y = padding.top + plotH - (plotH * d.total) / maxVal;
    return { x, y, ...d };
  });

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1]?.x ?? 0} ${padding.top + plotH} L ${points[0]?.x ?? 0} ${padding.top + plotH} Z`;

  if (!hasSales) {
    return <p className="mt-10 text-center text-sm text-gray-400">Aún no hay ventas registradas esta semana.</p>;
  }

  return (
    <div className="relative mt-3">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label="Ventas de los últimos 7 días">
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line
            key={f}
            x1={padding.left}
            x2={width - padding.right}
            y1={padding.top + plotH * (1 - f)}
            y2={padding.top + plotH * (1 - f)}
            stroke="#f1f5f9"
            strokeWidth={1}
          />
        ))}
        <path d={areaPath} fill="#1baf7a" opacity={0.08} />
        <path d={linePath} fill="none" stroke="#1baf7a" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={hoverIndex === i ? 5 : 3} fill="#1baf7a" stroke="white" strokeWidth={1.5} />
            <circle
              cx={p.x}
              cy={p.y}
              r={12}
              fill="transparent"
              onMouseEnter={() => setHoverIndex(i)}
              onMouseLeave={() => setHoverIndex((cur) => (cur === i ? null : cur))}
              style={{ cursor: "pointer" }}
            />
            <text x={p.x} y={height - 4} textAnchor="middle" fontSize={10} fill="#94a3b8">
              {p.day}
            </text>
          </g>
        ))}
      </svg>
      {hoverIndex !== null && points[hoverIndex] && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-md bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-lg"
          style={{ left: `${(points[hoverIndex].x / width) * 100}%`, top: `${(points[hoverIndex].y / height) * 100 - 2}%` }}
        >
          {points[hoverIndex].day}: {currency.format(points[hoverIndex].total)} ({points[hoverIndex].count})
        </div>
      )}
    </div>
  );
}

function CashShiftPanel({ cashShift }: { cashShift: StoreDashboardSummary["cashShift"] }) {
  if (!cashShift.isOpen) {
    return (
      <div className="mt-6 text-center">
        <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400">
          <Banknote size={18} />
        </span>
        <p className="mt-3 text-sm text-gray-500">No hay un turno de caja abierto.</p>
        <Link to="/store/pos" className="mt-2 inline-block text-xs font-medium text-brand-600 hover:text-brand-800">
          Abrir caja
        </Link>
      </div>
    );
  }

  const openedLabel = new Date(cashShift.openedAt as string).toLocaleString("es-CO", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <div className="mt-3 space-y-3">
      <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Caja abierta desde {openedLabel}
      </div>
      <div className="space-y-1.5 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Base inicial</span>
          <span className="font-medium text-gray-900">{currency.format(cashShift.openingCash)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Ventas en efectivo</span>
          <span className="font-medium text-gray-900">{currency.format(cashShift.cashSalesTotal)}</span>
        </div>
        <div className="flex items-center justify-between border-t border-gray-100 pt-1.5">
          <span className="text-gray-500">Efectivo esperado</span>
          <span className="font-semibold text-gray-900">{currency.format(cashShift.expectedCash)}</span>
        </div>
      </div>
      <Link to="/store/pos" className="block text-center text-xs font-medium text-brand-600 hover:text-brand-800">
        Ir a punto de venta
      </Link>
    </div>
  );
}

function RecentSalesList({ sales }: { sales: StoreDashboardSummary["recentSales"] }) {
  if (sales.length === 0) {
    return <p className="mt-6 text-center text-sm text-gray-400">Aún no hay ventas registradas.</p>;
  }
  return (
    <div className="mt-3 space-y-2">
      {sales.map((s) => (
        <div key={s.id} className="flex items-center justify-between rounded-lg px-1 py-1.5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-600">
              <ShoppingCart size={15} />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Venta #{s.saleNumber}</p>
              <p className="text-[11px] text-gray-400">
                {timeAgo(s.saleDate)} · {PAYMENT_METHOD_LABEL[s.paymentMethod] ?? s.paymentMethod}
              </p>
            </div>
          </div>
          <span className="text-sm font-semibold text-gray-900">{currency.format(s.total)}</span>
        </div>
      ))}
    </div>
  );
}

function LowStockList({ products, byBranch }: { products: StoreDashboardSummary["lowStockProducts"]; byBranch: StoreDashboardSummary["lowStockByBranch"] }) {
  if (products.length === 0) {
    return <p className="mt-6 text-center text-sm text-gray-400">Todo el inventario está por encima del mínimo.</p>;
  }
  return (
    <div className="mt-3 space-y-3">
      {byBranch.length > 1 && (
        <div className="flex flex-wrap gap-1.5 border-b border-gray-100 pb-2.5">
          {byBranch.map((b) => (
            <span key={b.branchId} className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
              {b.branchName}: {b.count} bajo{b.count === 1 ? "" : "s"}
            </span>
          ))}
        </div>
      )}
      {products.map((p) => (
        <div key={p.id} className="flex items-center justify-between rounded-lg px-1 py-1.5">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-600">
              <PackageX size={15} />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-900">{p.name}</p>
              <p className="text-[11px] text-gray-400">SKU {p.sku}</p>
            </div>
          </div>
          <span className="shrink-0 text-sm font-semibold text-red-600">
            {p.stockQty} / {p.minStock}
          </span>
        </div>
      ))}
    </div>
  );
}

function TopProductsList({ products }: { products: StoreDashboardSummary["topProducts"] }) {
  if (products.length === 0) {
    return <p className="mt-6 text-center text-sm text-gray-400">Aún no hay ventas suficientes (últimos 30 días).</p>;
  }
  return (
    <div className="mt-3 space-y-2">
      {products.map((p, i) => (
        <div key={p.id} className="flex items-center justify-between rounded-lg px-1 py-1.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[11px] font-semibold text-brand-700">
              {i + 1}
            </span>
            <p className="truncate text-sm font-medium text-gray-900">{p.name}</p>
          </div>
          <span className="shrink-0 text-xs text-gray-500">
            {p.quantitySold} u · {currency.format(p.revenue)}
          </span>
        </div>
      ))}
    </div>
  );
}
